import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import FilePicker from "../components/FilePicker";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function PdfRedact() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [terms, setTerms] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function suggest() {
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/redact-suggestions", { file_id: file.id });
      setTerms((data.terms || []).join("\n"));
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  async function run() {
    if (!file || !terms.trim()) {
      toast.error(t("redact_terms"));
      return;
    }
    setBusy(true);
    try {
      // Pull file content via library endpoint - download then upload to /pdf/redact (multipart).
      const blob = await (await api.get(`/files/${file.id}/download`, { responseType: "blob" })).data;
      const fd = new FormData();
      fd.append("file", new File([blob], file.original_filename, { type: "application/pdf" }));
      fd.append("terms", terms);
      const { data } = await api.post("/pdf/redact", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-redact-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_redact")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_redact_desc")}</p>

      <div className="mt-8 grid gap-6">
        {!file ? (
          <FilePicker accept="application/pdf" filter={(f) => f.ext === "pdf"} onSelected={(f) => setFile(f)} />
        ) : (
          <>
            <div className="border border-zinc-200 rounded-md p-6 bg-white">
              <span className="eyebrow">FILE</span>
              <div className="hero-h2 text-xl mt-1">{file.original_filename}</div>
              <Button variant="ghost" className="rounded-sm mt-3" onClick={() => { setFile(null); setResult(null); setTerms(""); }}>
                {t("cta_clear")}
              </Button>
            </div>

            <div>
              <div className="flex justify-between items-end">
                <Label className="eyebrow">{t("redact_terms")}</Label>
                <Button onClick={suggest} disabled={busy} variant="outline" className="rounded-sm border-zinc-300"
                  data-testid="redact-suggest">
                  ✨ {t("redact_suggest_btn")}
                </Button>
              </div>
              <textarea value={terms} onChange={(e) => setTerms(e.target.value)}
                placeholder="email@example.com&#10;Juan Pérez&#10;12345678A"
                className="w-full mt-2 rounded-sm border border-zinc-300 p-3 min-h-[160px] font-light mono text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black"
                data-testid="redact-terms" />
            </div>

            <Button onClick={run} disabled={busy || !terms.trim()}
              className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11 w-fit"
              data-testid="redact-run">
              {busy ? t("processing") : `${t("cta_apply")} →`}
            </Button>
          </>
        )}

        {result && (
          <div className="border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="redact-result">
            <span className="eyebrow">{t("result_ready")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <Button onClick={() => downloadFile(result.id, result.original_filename)}
              className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
              ↓ {t("cta_download")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
